import {
  MedicationOutput,
  useGetMedicationsQuery,
} from 'graphql/generated/hasura';
import { NetworkStatus } from '@apollo/client';
import { useMemo } from 'react';
import debounce from 'lodash/debounce';
import { InputActionMeta } from 'react-select';

const DEBOUNCE_TIME = 600;

export const useGetSelfMedication = (isSaved: boolean, isMulti = false) => {
  const { fetchMore, loading, data, refetch, networkStatus } =
    useGetMedicationsQuery({
      skip: isSaved,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: { limit: 20, offset: 0 },
    });

  const isFetchingMore = networkStatus === NetworkStatus.fetchMore;

  const medicationsList = useMemo(() => {
    const fetchedMedications = data?.getMedications?.medications ?? [];
    return fetchedMedications.map((med: MedicationOutput) => {
      return {
        value: med.productNdc as string,
        label: `${med.proprietaryName} ${
          med?.activeNumeratorStrength ? '(' + med?.activeNumeratorStrength : ''
        } ${med?.activeIngredUnit ? med?.activeIngredUnit + ')' : ''}`,
      };
    });
  }, [data]);

  const medicationsListLabels = useMemo(() => {
    const fetchedMedications = data?.getMedications?.medications ?? [];
    return fetchedMedications.map((med: MedicationOutput) => {
      const value = `${med.proprietaryName} ${
        med?.activeNumeratorStrength ? '(' + med?.activeNumeratorStrength : ''
      } ${med?.activeIngredUnit ? med?.activeIngredUnit + ')' : ''}`;
      return {
        value: value,
        label: value,
      };
    });
  }, [data]);

  const handleLoadMoreMedications = () => {
    if (loading || isFetchingMore) return;
    fetchMore({
      variables: {
        offset: medicationsList?.length || 0,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          ...prev,
          getMedications: {
            medications: [
              ...(prev.getMedications?.medications ?? []),
              ...(fetchMoreResult.getMedications?.medications ?? []),
            ],
          },
        };
      },
    });
  };

  const handleSearchChange = debounce(
    (newValue: string, actionMeta: InputActionMeta) => {
      if (actionMeta.action === 'input-change') {
        refetch({ offset: 0, search: newValue });
      }
      if (isMulti && actionMeta.action === 'menu-close') {
        refetch({ offset: 0, search: '' });
      }
    },
    DEBOUNCE_TIME,
  );

  return {
    loading,
    handleLoadMoreMedications,
    handleSearchChange,
    medicationsList,
    medicationsListLabels,
    isFetchingMore,
  };
};
